import { apiFetch, isApiMode, TENANT_HEADER } from './http';

// ─────────────────────────────────────────────────────────────────────
// A CLUB BOOKS A CENTRE'S COURTS THROUGH A CO-OPERATION — the client
// (slice 4c, 2026-10-20).
//
// `/link/bookings` is scoped to the CENTRE: every call names it in the tenant
// header, overriding the club the portal is acting in (`apiFetch` sets the
// header only when the caller has not). The club the booking is for is the
// server's — it comes from the link the evaluator found — and is named here
// only to pick between several, with `?home=`, which identifies and grants
// nothing. Everything here is a VIEW of what the server decided: the times,
// the courts, the window and the club's charge are all the server's.
// ─────────────────────────────────────────────────────────────────────

export interface LinkSlot {
  startTime: string;
  endTime: string;
  /** The free court numbers at that time — never who holds the others (2A). */
  courts: number[];
  /** What the centre invoices the club, in øre (5A). The player owes nothing. */
  chargeOre: number;
}

export interface LinkSlots {
  host: { id: string; name: string };
  home: { id: string; name: string };
  windowDays: number;
  dates: string[];
  durations: number[];
  date: string;
  durationMin: number;
  /** True when the agreed partner rate prices these slots. */
  partnerRate: boolean;
  slots: LinkSlot[];
}

export interface LinkBooking {
  id: string;
  court: number;
  date: string;
  startTime: string;
  endTime: string | null;
  durationMin: number | null;
  chargeOre: number | null;
}

export type LinkRead<T> =
  | { state: 'no-api' }
  | { state: 'ok'; value: T }
  | { state: 'forbidden' }
  /** A machine code — `link_not_active`, `date_outside_booking_window` … */
  | { state: 'refused'; reason: string }
  | { state: 'error'; status: number | null };

export type LinkWrite =
  | { state: 'ok' }
  | { state: 'forbidden' }
  | { state: 'refused'; reason: string }
  | { state: 'error'; status: number | null };

const at = (hostId: string, extra: Record<string, string> = {}) => ({
  [TENANT_HEADER]: hostId,
  ...extra,
});
const query = (params: Record<string, string | number | null | undefined>) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params))
    if (v !== null && v !== undefined && v !== '') q.set(k, String(v));
  const s = q.toString();
  return s ? `?${s}` : '';
};

async function read<T>(
  path: string,
  hostId: string,
  pick: (body: unknown) => T,
): Promise<LinkRead<T>> {
  if (!isApiMode()) return { state: 'no-api' };
  try {
    const res = await apiFetch(path, { headers: at(hostId) });
    const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    if (res.ok) return { state: 'ok', value: pick(body) };
    const reason = typeof body?.error === 'string' ? body.error : null;
    if (res.status === 403 && reason === 'link_not_active') return { state: 'refused', reason };
    if (res.status === 401 || res.status === 403) return { state: 'forbidden' };
    if (res.status === 400 || res.status === 409)
      return { state: 'refused', reason: reason ?? 'refused' };
    return { state: 'error', status: res.status };
  } catch {
    return { state: 'error', status: null };
  }
}

async function write(path: string, hostId: string, init: RequestInit): Promise<LinkWrite> {
  if (!isApiMode()) return { state: 'error', status: null };
  try {
    const res = await apiFetch(path, {
      ...init,
      headers: { ...at(hostId), ...(init.body ? { 'Content-Type': 'application/json' } : {}) },
    });
    if (res.ok) return { state: 'ok' };
    const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    const reason = typeof body?.error === 'string' ? body.error : null;
    // Named 403s are rules, not a missing right.
    if (
      res.status === 403 &&
      (reason === 'link_not_active' || reason === 'reauthentication_required')
    ) {
      return { state: 'refused', reason };
    }
    if (res.status === 401 || res.status === 403) return { state: 'forbidden' };
    if (res.status === 400 || res.status === 404 || res.status === 409) {
      return { state: 'refused', reason: reason ?? 'refused' };
    }
    return { state: 'error', status: res.status };
  } catch {
    return { state: 'error', status: null };
  }
}

/** What may be booked at this centre on `date`, for `durationMin`. */
export function fetchLinkSlots(
  hostId: string,
  opts: { date?: string; durationMin?: number; home?: string | null } = {},
): Promise<LinkRead<LinkSlots>> {
  return read(
    `/link/bookings/slots${query({ date: opts.date, durationMin: opts.durationMin, home: opts.home })}`,
    hostId,
    (b) => b as LinkSlots,
  );
}

/** The club's own upcoming bookings at this centre. */
export function fetchLinkBookings(
  hostId: string,
  home: string | null = null,
): Promise<LinkRead<LinkBooking[]>> {
  return read(
    `/link/bookings${query({ home })}`,
    hostId,
    (b) => (b as { bookings?: LinkBooking[] })?.bookings ?? [],
  );
}

/** Book one slot for the club. The server decides the club, the window and the charge. */
export function bookLinkSlot(
  hostId: string,
  slot: { court: number; date: string; startTime: string; durationMin: number; requestId: string },
  home: string | null = null,
): Promise<LinkWrite> {
  return write(`/link/bookings${query({ home })}`, hostId, {
    method: 'POST',
    body: JSON.stringify(slot),
  });
}

/** Cancel one of the club's own bookings here (3A). */
export function cancelLinkBooking(
  hostId: string,
  id: string,
  home: string | null = null,
): Promise<LinkWrite> {
  return write(`/link/bookings/${encodeURIComponent(id)}${query({ home })}`, hostId, {
    method: 'DELETE',
  });
}
